import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from 'react';
import { Box, GlobalStyles } from '@mui/material';
import maplibregl, { type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import Map, {
  Source,
  Layer,
  Marker,
  Popup,
  NavigationControl,
  MapRef,
} from 'react-map-gl/maplibre';
import { useColorMode } from '@/styles/theme';

import dncMarkerUrl from '../assets/DNC-marker.png';
import warning2Url from '../assets/warning2.png';
import marker10Url from '../assets/10-marker.png';
import marker20Url from '../assets/20-marker.png';
import marker30Url from '../assets/30-marker.png';
import marker50Url from '../assets/50-marker.png';
import marker90Url from '../assets/90-marker.png';
import markerUrl from '../assets/marker.png';

const SATELLITE_VECTOR_ZOOM_THRESHOLD = 13;

type MapboxStyle = StyleSpecification;

type MergedStyleResult = {
  style: MapboxStyle;
  baseLayerIds: string[];
  satLayerIds: string[];
};

function mergeStadiaStyles(
  smooth: MapboxStyle,
  satellite: MapboxStyle,
  basePrefix = 'base-',
  satPrefix = 'sat-',
): MergedStyleResult {
  const merged: MapboxStyle = {
    ...smooth,
    sources: { ...(smooth.sources || {}) },
    layers: [],
    metadata: {
      ...(smooth.metadata || {}),
      'iapp:hasMergedSatellite': true,
    },
  };

  const baseLayerIds: string[] = [];
  const satLayerIds: string[] = [];

  (smooth.layers || []).forEach((layer: any) => {
    const newId = `${basePrefix}${layer.id}`;
    const newLayer: any = { ...layer, id: newId };

    if (layer.ref) {
      newLayer.ref = `${basePrefix}${layer.ref}`;
    }

    merged.layers!.push(newLayer);
    baseLayerIds.push(newId);
  });

  Object.entries(satellite.sources || {}).forEach(([id, source]) => {
    const newId = `${satPrefix}${id}`;
    merged.sources![newId] = source as any;
  });

  (satellite.layers || []).forEach((layer: any) => {
    const newId = `${satPrefix}${layer.id}`;
    const newLayer: any = { ...layer, id: newId };

    if (layer.source) {
      newLayer.source = `${satPrefix}${layer.source}`;
    }

    if (layer.ref) {
      newLayer.ref = `${satPrefix}${layer.ref}`;
    } else {
      newLayer.layout = {
        ...(layer.layout || {}),
        visibility: 'none', // start all sat-* layers hidden
      };
    }

    merged.layers!.push(newLayer);
    satLayerIds.push(newId);
  });

  return { style: merged, baseLayerIds, satLayerIds };
}

function setLayerGroupVisibility(
  map: maplibregl.Map,
  layerIds: string[],
  visible: boolean,
) {
  const visibility = visible ? 'visible' : 'none';
  layerIds.forEach((id) => {
    if (!map.getLayer(id)) return;
    try {
      map.setLayoutProperty(id, 'visibility', visibility);
    } catch {
      // ignore errors for layers that don't support visibility
    }
  });
}

/*******************************
 * Types & Interfaces
 *******************************/
interface CustomMapProps {
  geojsonData: GeoJSON.FeatureCollection[];
  fiftyMaxData: GeoJSON.FeatureCollection[]; // [ linestring, redPoints, blackPoints, polygons ]
  hpmvData: GeoJSON.FeatureCollection[]; // [ line1, line2, points ]
  tilesetIds: string[]; // Mapbox tilesets: e.g. "user.tilesetid"
  rcaTilesetIds: string[]; // Mapbox tilesets
  startPoint?: [number, number] | null; // [lng, lat]
  endPoint?: [number, number] | null; // [lng, lat]
  waypoints: [number, number][]; // [lng, lat][]
  setWaypoints: React.Dispatch<React.SetStateAction<[number, number][]>>;
  isWaypointMode: boolean;
  showHighRiskOnly: boolean;
  displayLeftBar: boolean;
  hasGeojsonData: boolean;
  councils: Array<{ id: string; name: string }>;
  selectedRegionalCouncils: {
    name: string;
    hasGeojsonData: boolean;
    hasUploads: boolean;
  }[];
  selectedTerritorialAuthorities: string[];
  showRegionalCouncils: boolean;
  showTerritorialAuthorities: boolean;
}

/*******************************
 * Helpers
 *******************************/
// Decode Valhalla polyline (polyline6 precision) -> returns [lng, lat][]
function decodeValhallaShape(encoded: string): [number, number][] {
  const coords: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lon = 0;
  const factor = 1e6;
  const len = encoded.length;
  while (index < len) {
    let result = 0;
    let shift = 0;
    let b: number;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    result = 0;
    shift = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lon += dlng;
    coords.push([lon / factor, lat / factor]); // [lng, lat]
  }
  return coords;
}

const makeStartEl = () => {
  const el = document.createElement('div');
  el.style.width = '40px';
  el.style.height = '40px';
  el.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
      <circle opacity="0.3" cx="32" cy="32" r="31.5" fill="#3C91E6" stroke="#3C91E6"/>
      <g transform="translate(21,21)">
        <path d="M11 21.2663C16.7906 21.2663 21.5 16.6522 21.5 10.9417C21.5 5.23134 16.7906 0.617188 11 0.617188C5.20939 0.617188 0.5 5.23134 0.5 10.9417C0.5 16.6522 5.20939 21.2663 11 21.2663ZM16.9286 10.9417C16.9286 14.0529 14.3411 16.6949 11 16.6949C7.65894 16.6949 5.07143 14.0529 5.07143 10.9417C5.07143 7.83061 7.65894 5.18862 11 5.18862C14.3411 5.18862 16.9286 7.83061 16.9286 10.9417Z" fill="#C2EFEB" stroke="#C2EFEB"/>
      </g>
    </svg>`;
  return el;
};

const makeEndEl = () => {
  const el = document.createElement('div');
  el.style.width = '27px';
  el.style.height = '26px';
  el.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="27" height="26" viewBox="0 0 27 26" fill="none">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M14.2127 25.0763C17.0127 22.7847 23.8783 16.6799 23.8783 11.9168C23.8783 5.93374 19.0281 1.0835 13.045 1.0835C7.06192 1.0835 2.21167 5.93374 2.21167 11.9168C2.21167 16.6799 9.07732 22.7847 11.8773 25.0763C12.5635 25.638 13.5265 25.638 14.2127 25.0763ZM13.045 15.1668C14.8399 15.1668 16.295 13.7118 16.295 11.9168C16.295 10.1219 14.8399 8.66683 13.045 8.66683C11.2501 8.66683 9.795 10.1219 9.795 11.9168C9.795 13.7118 11.2501 15.1668 13.045 15.1668Z" fill="#F6AE2D"/>
    </svg>`;
  return el;
};

const makeWaypointEl = (index: number) => {
  const el = document.createElement('div');
  el.style.width = '30px';
  el.style.height = '30px';
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.innerHTML = `
    <div style="position:relative; width:30px; height:30px; display:flex; align-items:center; justify-content:center;">
      <div style="position:absolute; top:3px; left:3px; width:24px; height:24px; background:rgb(246,174,45); border-radius:50%; border:2px solid #222; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:12px; color:#000;">${index + 1}</div>
    </div>`;
  return el;
};

function featurePopupHTML(props: Record<string, any>, mode: 'light' | 'dark') {
  const headingText =
    props.bridgename || props.bridgeName || 'Bridge Information';

  const paragraph = (label: string, key: string, suffix = '') => {
    const v = props[key];
    if (v === undefined || v === null || `${v}`.trim() === '') return '';
    return `<p style="margin:2px 0; padding:0; line-height:1.3; font-size:15px;"><strong>${label}:</strong> ${v}${suffix}</p>`;
  };

  const body =
    paragraph('RCA', 'RCA') +
    paragraph('Road Name', 'roadName') +
    paragraph('Road ID', 'roadID') +
    paragraph('Bridge No2', 'bridgeNo2') +
    paragraph('Bridge No', 'bridgeNo') +
    paragraph('Bridge ID', 'bridgeID') +
    paragraph('Owner', 'owner') +
    paragraph('Easting', 'easting') +
    paragraph('Northing', 'northing') +
    paragraph('Max HPMV', 'maxHPMV') +
    paragraph('Island', 'island') +
    paragraph('Status', 'status') +
    paragraph('Removed', 'removed') +
    paragraph('Area', 'area') +
    paragraph('Risk', 'risk') +
    paragraph('Permit number', 'permit') +
    paragraph('Expiry', 'expiry') +
    paragraph('Speed', 'speed', ' km/hr') +
    paragraph('Position', 'position') +
    paragraph('Coordinates', 'coordinates') +
    paragraph('Notes', 'notes') +
    paragraph('VAI', 'vai');

  return `
    <div class="custom-popup ${mode}-mode" style="max-width:320px">
      <h3 style="color:#f6ae2d; font-size:16px; margin:0 0 10px; font-weight:bold;">${headingText}</h3>
      ${body}
    </div>
  `;
}

/*******************************
 * Main Component (MapLibre)
 *******************************/
const MapStadia_MapLibre: React.FC<CustomMapProps> = ({
  geojsonData = [],
  fiftyMaxData = [],
  hpmvData = [],
  tilesetIds = [],
  rcaTilesetIds = [],
  startPoint = null,
  endPoint = null,
  waypoints,
  setWaypoints,
  isWaypointMode,
  showHighRiskOnly,
  displayLeftBar,
  selectedRegionalCouncils = [],
  selectedTerritorialAuthorities = [],
  showRegionalCouncils = false,
  showTerritorialAuthorities = false,
}) => {
  const { mode } = useColorMode();

  const mapRef = useRef<MapRef | null>(null);

  const [iconsLoaded, setIconsLoaded] = useState<boolean>(false);

  const [mapLoaded, setMapLoaded] = useState(false);

  const [mergedStyle, setMergedStyle] = useState<MapboxStyle | null>(null);
  const [layerGroups, setLayerGroups] = useState<{
    baseLayerIds: string[];
    satLayerIds: string[];
  } | null>(null);

  const loadLocalIcons = useCallback(async (map: maplibregl.Map) => {
    const iconMappings = {
      'DNC-marker': dncMarkerUrl,
      warning2: warning2Url,
      '10-marker': marker10Url,
      '20-marker': marker20Url,
      '30-marker': marker30Url,
      '50-marker': marker50Url,
      '90-marker': marker90Url,
      marker: markerUrl,
    };

    const loadPromises = Object.entries(iconMappings).map(
      async ([iconName, iconPath]) => {
        try {
          if (map.hasImage(iconName)) {
            return { iconName, success: true };
          }
          const image = await map.loadImage(iconPath);
          if (!map.hasImage(iconName)) {
            map.addImage(iconName, image.data);
          }
          return { iconName, success: true };
        } catch (error) {
          console.error(
            `Failed to load icon ${iconName} from ${iconPath}:`,
            error,
          );
          return { iconName, success: false, error };
        }
      },
    );

    const results = await Promise.all(loadPromises);
    const successful = results.filter((r) => r.success);

    setIconsLoaded(true);

    return {
      successful: successful.length,
      failed: results.length - successful.length,
      total: results.length,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const smoothUrl =
      mode === 'dark'
        ? 'https://tiles.stadiamaps.com/styles/alidade_smooth_dark.json'
        : 'https://tiles.stadiamaps.com/styles/alidade_smooth.json';

    const satelliteUrl =
      'https://tiles.stadiamaps.com/styles/alidade_satellite.json';

    (async () => {
      try {
        const [smoothRes, satRes] = await Promise.all([
          fetch(smoothUrl).then((r) => r.json()),
          fetch(satelliteUrl).then((r) => r.json()),
        ]);

        if (cancelled) return;

        const { style, baseLayerIds, satLayerIds } = mergeStadiaStyles(
          smoothRes,
          satRes,
        );

        setMergedStyle(style);
        setLayerGroups({ baseLayerIds, satLayerIds });
      } catch (e) {
        console.error('Failed to load/merge Stadia styles', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode]);

  const pointsSourceData = useMemo<GeoJSON.FeatureCollection>(() => {
    const pointFeatures: GeoJSON.Feature[] = [];

    geojsonData.forEach((item) => {
      if (!item) return;
      item.features.forEach((feature) => {
        pointFeatures.push(feature);
      });
    });

    return {
      type: 'FeatureCollection',
      features: pointFeatures,
    };
  }, [geojsonData]);

  const [routeCoords, setRouteCoords] = useState<[number, number][]>([]);
  const [localWaypoints, setLocalWaypoints] =
    useState<[number, number][]>(waypoints);
  useEffect(() => setLocalWaypoints(waypoints), [waypoints]);

  const [popupInfo, setPopupInfo] = useState<{
    lngLat: [number, number];
    html: string;
  } | null>(null);

  const addWaypoint = useCallback(
    (coord: [number, number]) => {
      setLocalWaypoints((prev) => {
        const updated = [...prev, coord];
        setWaypoints(updated);
        return updated;
      });
    },
    [setWaypoints],
  );

  const updateWaypoint = (index: number, coord: [number, number]) => {
    setLocalWaypoints((prev) => {
      const updated = [...prev];
      updated[index] = coord;
      setWaypoints(updated);
      return updated;
    });
  };

  const removeWaypoint = (index: number) => {
    setLocalWaypoints((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      setWaypoints(updated);
      return updated;
    });
  };

  const onMapClick = useCallback(
    (e: any) => {
      if (!isWaypointMode) return;
      const { lng, lat } = e.lngLat;
      addWaypoint([lng, lat]);
    },
    [isWaypointMode, addWaypoint],
  );

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !mergedStyle) {
      return;
    }

    const map = mapRef.current.getMap();

    const handleStyleLoad = async () => {
      try {
        const iconResults = await loadLocalIcons(map);

        if (iconResults.successful === 0) {
          console.error(
            'No icons loaded successfully, skipping layer creation',
          );
          return;
        }

        const cleanupExistingLayers = () => {
          const style = map.getStyle();
          if (style && style.layers) {
            const existingLayerIds = style.layers.map((layer) => layer.id);
            existingLayerIds.forEach((layerId) => {
              if (
                layerId.startsWith('points-layer-') ||
                layerId.startsWith('points-numbers-') ||
                layerId.startsWith('polygon-layer-')
              ) {
                if (map.getLayer(layerId)) {
                  try {
                    map.removeLayer(layerId);
                  } catch (e) {
                    console.warn(`Failed to remove layer ${layerId}:`, e);
                  }
                }
              }
            });
          }

          if (style && style.sources) {
            Object.keys(style.sources).forEach((sourceId) => {
              if (sourceId.startsWith('geojson-data-')) {
                if (map.getSource(sourceId)) {
                  try {
                    map.removeSource(sourceId);
                  } catch (e) {
                    console.warn(`Failed to remove source ${sourceId}:`, e);
                  }
                }
              }
            });
          }
        };

        const addDataLayers = () => {
          if (pointsSourceData.features.length > 0) {
            const pointsSourceId = 'geojson-data-points';

            try {
              map.addSource(pointsSourceId, {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: pointsSourceData.features,
                },
              });

              const pointsLayerId = 'points-layer-main';
              map.addLayer({
                id: pointsLayerId,
                type: 'symbol',
                source: pointsSourceId,
                layout: {
                  'icon-image': [
                    'case',
                    ['==', ['downcase', ['get', 'position']], 'do not cross'],
                    'DNC-marker',
                    ['==', ['get', 'speed'], 'N/A'],
                    'warning2',
                    [
                      'match',
                      ['to-number', ['get', 'speed'], 0],
                      0,
                      'DNC-marker',
                      10,
                      '10-marker',
                      20,
                      '20-marker',
                      30,
                      '30-marker',
                      50,
                      '50-marker',
                      90,
                      '90-marker',
                      'marker',
                    ],
                  ],
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true,
                  'icon-size': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    5,
                    0.4,
                    12,
                    0.8,
                    14,
                    1,
                  ],
                  'icon-anchor': [
                    'case',
                    ['==', ['get', 'speed'], 'N/A'],
                    'top',
                    'bottom',
                  ],
                },
              });

              if (showHighRiskOnly) {
                map.setFilter(pointsLayerId, [
                  '==',
                  ['downcase', ['get', 'position']],
                  'do not cross',
                ]);
              }

              map.on('click', pointsLayerId, (e: any) => {
                if (e.features && e.features.length > 0) {
                  const coordinates =
                    e.features[0].geometry.coordinates.slice();
                  const properties = e.features[0].properties;

                  setPopupInfo({
                    lngLat: coordinates,
                    html: featurePopupHTML(
                      properties,
                      mode as 'light' | 'dark',
                    ),
                  });
                }
              });

              map.on('mouseenter', pointsLayerId, () => {
                map.getCanvas().style.cursor = 'pointer';
              });

              map.on('mouseleave', pointsLayerId, () => {
                map.getCanvas().style.cursor = '';
              });
            } catch (error) {
              console.error('Failed to add geojson layers:', error);
            }
          }
        };

        cleanupExistingLayers();
        addDataLayers();
      } catch (error) {
        console.error('Error in handleStyleLoad:', error);
      }
    };

    if (map.isStyleLoaded()) {
      handleStyleLoad();
    } else {
      map.once('styledata', handleStyleLoad);
    }

    return () => {
      map.off('styledata', handleStyleLoad);
    };
  }, [
    mapLoaded,
    mergedStyle,
    loadLocalIcons,
    pointsSourceData,
    mode,
    setPopupInfo,
  ]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !layerGroups) return;

    const map = mapRef.current.getMap();
    const { baseLayerIds, satLayerIds } = layerGroups;

    // Single zoom threshold where we switch from smooth -> satellite
    const Z_SWITCH = SATELLITE_VECTOR_ZOOM_THRESHOLD;

    const update = () => {
      const z = map.getZoom();

      const showSatellite = z >= Z_SWITCH;
      const showBase = !showSatellite;

      setLayerGroupVisibility(map, baseLayerIds, showBase);
      setLayerGroupVisibility(map, satLayerIds, showSatellite);
    };

    // Run once immediately to set initial visibility
    update();
    // Then keep it in sync on zoom
    map.on('zoom', update);

    return () => {
      map.off('zoom', update);
    };
  }, [mapLoaded, layerGroups]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) {
      return;
    }

    const map = mapRef.current.getMap();

    const applyFilters = () => {
      const pointsLayerId = 'points-layer-main';

      if (map.getLayer(pointsLayerId)) {
        try {
          if (showHighRiskOnly) {
            map.setFilter(pointsLayerId, [
              '==',
              ['downcase', ['get', 'position']],
              'do not cross',
            ]);
          } else {
            map.setFilter(pointsLayerId, null);
          }
        } catch (error) {
          console.warn('Failed to apply filter:', error);
        }
      }
    };

    // Apply filters with a small delay to ensure layer exists
    const timeoutId = setTimeout(applyFilters, 100);

    return () => clearTimeout(timeoutId);
  }, [mapLoaded, showHighRiskOnly]);

  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.resize(), 300);
    return () => clearTimeout(t);
  }, [displayLeftBar]);

  useEffect(() => {
    const fetchRoute = async () => {
      if (!startPoint || !endPoint) {
        setRouteCoords([]);
        return;
      }
      try {
        const chain = [startPoint, ...localWaypoints, endPoint];
        const body = {
          locations: chain.map(([lng, lat]) => ({
            lon: lng,
            lat,
            type: 'break',
          })),
          costing: 'auto',
          units: 'kilometers',
          shape_format: 'polyline6',
        };
        const res = await fetch('/api/v1/customer/map-route/v1', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          console.error('Routing HTTP error', res.status, await res.text());
          setRouteCoords([]);
          return;
        }
        const data = await res.json();
        const trip = data?.trip;
        const legs = trip?.legs;
        const collected: [number, number][] = [];
        if (Array.isArray(legs) && legs.length) {
          legs.forEach((leg: any) => {
            const shape = leg?.shape;
            if (!shape) return;
            if (
              shape?.type === 'LineString' &&
              Array.isArray(shape.coordinates)
            ) {
              collected.push(...shape.coordinates);
            } else if (typeof shape === 'string') {
              collected.push(...decodeValhallaShape(shape));
            }
          });
        } else if (trip?.shape) {
          const s = trip.shape;
          if (s?.type === 'LineString' && Array.isArray(s.coordinates)) {
            collected.push(...s.coordinates);
          } else if (typeof s === 'string') {
            collected.push(...decodeValhallaShape(s));
          }
        }
        setRouteCoords(collected);
      } catch (e) {
        console.error('Route fetch error', e);
        setRouteCoords([]);
      }
    };
    fetchRoute();
  }, [startPoint, endPoint, localWaypoints]);

  const initialCenter: [number, number] = useMemo(() => {
    if (startPoint) return startPoint;
    // fallback: first point from geojsonData, else Auckland-ish
    for (const fc of geojsonData) {
      if (!fc) continue;
      for (const f of fc.features) {
        if (f.geometry?.type === 'Point') {
          return f.geometry.coordinates as [number, number];
        }
      }
    }
    return [174.76555, -36.84846];
  }, [startPoint, geojsonData]);

  /**************
   * Derived GeoJSON (for layers & markers)
   **************/
  const routeGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    return {
      type: 'FeatureCollection',
      features: routeCoords.length
        ? [
            {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: routeCoords,
              },
              properties: {},
            },
          ]
        : [],
    };
  }, [routeCoords]);

  /**************
   * Popup helper for DOM markers
   **************/
  const openFeaturePopup = useCallback(
    (coord: [number, number], props: Record<string, any>) => {
      setPopupInfo({
        lngLat: coord,
        html: featurePopupHTML(props, mode as 'light' | 'dark'),
      });
    },
    [mode],
  );

  // Popup click handlers for HPMV layers
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) {
      return;
    }

    const map = mapRef.current.getMap();

    const hpmvLine1ClickHandler = (e: any) => {
      if (e.features && e.features.length > 0) {
        const geom = e.features[0].geometry;
        let mid = [];
        if (geom.type === 'LineString') {
          mid = geom.coordinates[Math.floor(geom.coordinates.length / 2)];
        } else if (geom.type === 'MultiLineString') {
          mid = geom.coordinates[0][Math.floor(geom.coordinates[0].length / 2)];
        }

        const props = e.features[0].properties;

        setPopupInfo({
          lngLat: mid,
          html: `<div>
          <h3 style="width:85%; margin-bottom:5px; font-size:16px">${
            props.fullRoadName || props.roadName || 'N/A'
          }</h3>
          ${
            props.routeExtent
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>Route Extent:</strong> ${props.routeExtent}</p>`
              : ''
          }
          ${
            props.roadType
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>Road Type:</strong> ${props.roadType}</p>`
              : ''
          }
          ${
            props.roadControllingAuthority
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>Authority:</strong> ${props.roadControllingAuthority}</p>`
              : ''
          }
          ${
            props.MOUStatus
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>MOU Status:</strong> ${props.MOUStatus}</p>`
              : ''
          }
        </div>`,
        });
      }
    };

    // Handler functions for HPMV line 2 (blue lines)
    const hpmvLine2ClickHandler = (e: any) => {
      if (e.features && e.features.length > 0) {
        const geom = e.features[0].geometry;
        let mid = [];
        if (geom.type === 'LineString') {
          mid = geom.coordinates[Math.floor(geom.coordinates.length / 2)];
        } else if (geom.type === 'MultiLineString') {
          mid = geom.coordinates[0][Math.floor(geom.coordinates[0].length / 2)];
        }

        const props = e.features[0].properties;

        setPopupInfo({
          lngLat: mid,
          html: `<div>
          <h3 style="width:85%; margin-bottom:5px; font-size:16px">${
            props.fullRoadName || props.roadName || 'Line'
          }</h3>
          ${
            props.routeExtent
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>Route Extent:</strong> ${props.routeExtent}</p>`
              : ''
          }
          ${
            props.roadType
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>Road Type:</strong> ${props.roadType}</p>`
              : ''
          }
          ${
            props.roadControllingAuthority
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>Authority:</strong> ${props.roadControllingAuthority}</p>`
              : ''
          }
          ${
            props.MOUStatus
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>MOU Status:</strong> ${props.MOUStatus}</p>`
              : ''
          }
        </div>`,
        });
      }
    };

    const hpmvLine1MouseEnterHandler = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const hpmvLine1MouseLeaveHandler = () => {
      map.getCanvas().style.cursor = '';
    };

    const hpmvLine2MouseEnterHandler = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const hpmvLine2MouseLeaveHandler = () => {
      map.getCanvas().style.cursor = '';
    };

    const addHpmvClickHandlers = () => {
      // Click handlers for HPMV line layers
      if (hpmvData[0]?.features?.length > 0) {
        map.on('click', 'hpmv-line1-layer', hpmvLine1ClickHandler);
        map.on('mouseenter', 'hpmv-line1-layer', hpmvLine1MouseEnterHandler);
        map.on('mouseleave', 'hpmv-line1-layer', hpmvLine1MouseLeaveHandler);
      }

      if (hpmvData[1]?.features?.length > 0) {
        map.on('click', 'hpmv-line2-layer', hpmvLine2ClickHandler);
        map.on('mouseenter', 'hpmv-line2-layer', hpmvLine2MouseEnterHandler);
        map.on('mouseleave', 'hpmv-line2-layer', hpmvLine2MouseLeaveHandler);
      }
    };

    if (map.isStyleLoaded()) {
      addHpmvClickHandlers();
    } else {
      map.once('styledata', addHpmvClickHandlers);
    }

    return () => {
      if (hpmvData[0]?.features?.length > 0) {
        map.off('click', 'hpmv-line1-layer', hpmvLine1ClickHandler);
        map.off('mouseenter', 'hpmv-line1-layer', hpmvLine1MouseEnterHandler);
        map.off('mouseleave', 'hpmv-line1-layer', hpmvLine1MouseLeaveHandler);
      }
      if (hpmvData[1]?.features?.length > 0) {
        map.off('click', 'hpmv-line2-layer', hpmvLine2ClickHandler);
        map.off('mouseenter', 'hpmv-line2-layer', hpmvLine2MouseEnterHandler);
        map.off('mouseleave', 'hpmv-line2-layer', hpmvLine2MouseLeaveHandler);
      }
      map.off('styledata', addHpmvClickHandlers);
    };
  }, [hpmvData, setPopupInfo, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) {
      return;
    }

    const map = mapRef.current.getMap();

    const fiftyLineClickHandler = (e: any) => {
      if (e.features && e.features.length > 0) {
        const coordinatesArray = e.features[0].geometry.coordinates;
        const middle =
          coordinatesArray[Math.floor(coordinatesArray.length / 2)];
        const properties = e.features[0].properties;

        setPopupInfo({
          lngLat: middle,
          html: `<div>
          <h3 style="width:85%; margin-bottom:5px; font-size:16px">${
            properties.name || properties.roadName || 'Line'
          }</h3>
          ${
            properties.RCA
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>RCA:</strong> ${properties.RCA}</p>`
              : ''
          }
          ${
            properties.Shape__Length
              ? `<p style="font-size:15px; margin-bottom:5px;"><strong>Length:</strong> ${properties.Shape__Length}</p>`
              : ''
          }
        </div>`,
        });
      }
    };

    const fiftyPolygonClickHandler = (e: any) => {
      if (e.features && e.features.length > 0) {
        const coordinates = e.lngLat;
        const properties = e.features[0].properties;

        setPopupInfo({
          lngLat: [coordinates.lng, coordinates.lat],
          html: `<div>
          <h3 style="margin:0; font-size:16px;">
            ${properties.TLAName || 'N/A'}
          </h3>
          ${
            properties.TLAID
              ? `<p style="font-size:15px; margin:5px 0;"><strong>TLAID: </strong>${properties.TLAID}</p>`
              : ''
          }
          ${
            properties.island
              ? `<p style="font-size:15px; margin:5px 0;"><strong>Island: </strong>${properties.island}</p>`
              : ''
          }
          ${
            properties.statusExternal
              ? `<p style="font-size:15px; margin:5px 0;"><strong>Status: </strong> ${properties.statusExternal}</p>`
              : ''
          }
        </div>`,
        });
      }
    };

    const fiftyLineMouseEnterHandler = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const fiftyLineMouseLeaveHandler = () => {
      map.getCanvas().style.cursor = '';
    };

    const fiftyPolygonMouseEnterHandler = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const fiftyPolygonMouseLeaveHandler = () => {
      map.getCanvas().style.cursor = '';
    };

    const addFiftyMaxClickHandlers = () => {
      if (fiftyMaxData[0]?.features?.length > 0) {
        map.on('click', 'fifty-line-layer', fiftyLineClickHandler);
        map.on('mouseenter', 'fifty-line-layer', fiftyLineMouseEnterHandler);
        map.on('mouseleave', 'fifty-line-layer', fiftyLineMouseLeaveHandler);
      }

      if (fiftyMaxData[3]?.features?.length > 0) {
        map.on('click', 'fifty-polygons-fill', fiftyPolygonClickHandler);
        map.on(
          'mouseenter',
          'fifty-polygons-fill',
          fiftyPolygonMouseEnterHandler,
        );
        map.on(
          'mouseleave',
          'fifty-polygons-fill',
          fiftyPolygonMouseLeaveHandler,
        );
      }
    };

    if (map.isStyleLoaded()) {
      addFiftyMaxClickHandlers();
    } else {
      map.once('styledata', addFiftyMaxClickHandlers);
    }

    return () => {
      if (fiftyMaxData[0]?.features?.length > 0) {
        map.off('click', 'fifty-line-layer', fiftyLineClickHandler);
        map.off('mouseenter', 'fifty-line-layer', fiftyLineMouseEnterHandler);
        map.off('mouseleave', 'fifty-line-layer', fiftyLineMouseLeaveHandler);
      }
      if (fiftyMaxData[3]?.features?.length > 0) {
        map.off('click', 'fifty-polygons-fill', fiftyPolygonClickHandler);
        map.off(
          'mouseenter',
          'fifty-polygons-fill',
          fiftyPolygonMouseEnterHandler,
        );
        map.off(
          'mouseleave',
          'fifty-polygons-fill',
          fiftyPolygonMouseLeaveHandler,
        );
      }
      map.off('styledata', addFiftyMaxClickHandlers);
    };
  }, [fiftyMaxData, setPopupInfo, mapLoaded]);

  if (!mergedStyle) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        Loading map…
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <GlobalStyles
        styles={{
          '.maplibregl-map': {
            backgroundColor: '#121212 !important',
            transition: 'all 0.3s ease-in-out',
          },

          '.custom-popup.light-mode .maplibregl-popup-content, .custom-popup.light-mode .mapboxgl-popup-content':
            {
              backgroundColor: '#ffffff !important',
              color: '#202d38 !important',
              paddingRight: '10px',
            },

          '.maplibregl-popup.custom-popup.maplibregl-popup-anchor-bottom .maplibregl-popup-tip, .mapboxgl-popup.custom-popup.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip':
            {
              borderTopColor: '#ffffff !important',
            },
          '.maplibregl-popup.custom-popup.maplibregl-popup-anchor-top .maplibregl-popup-tip, .mapboxgl-popup.custom-popup.mapboxgl-popup-anchor-top .mapboxgl-popup-tip':
            {
              borderBottomColor: '#ffffff !important',
            },
          '.maplibregl-popup.custom-popup.maplibregl-popup-anchor-left .maplibregl-popup-tip, .mapboxgl-popup.custom-popup.mapboxgl-popup-anchor-left .mapboxgl-popup-tip':
            {
              borderRightColor: '#ffffff !important',
            },
          '.maplibregl-popup.custom-popup.maplibregl-popup-anchor-right .maplibregl-popup-tip, .mapboxgl-popup.custom-popup.mapboxgl-popup-anchor-right .mapboxgl-popup-tip':
            {
              borderLeftColor: '#ffffff !important',
            },

          '.custom-popup.dark-mode .maplibregl-popup-content, .custom-popup.dark-mode .mapboxgl-popup-content':
            {
              backgroundColor: 'rgb(32, 45, 56) !important',
              color: '#fff !important',
            },

          '.maplibregl-popup.custom-popup.maplibregl-popup-anchor-bottom.dark-mode .maplibregl-popup-tip, .mapboxgl-popup.custom-popup.mapboxgl-popup-anchor-bottom.dark-mode .mapboxgl-popup-tip':
            {
              borderTopColor: '#121212 !important',
            },
          '.maplibregl-popup.custom-popup.maplibregl-popup-anchor-top.dark-mode .maplibregl-popup-tip, .mapboxgl-popup.custom-popup.mapboxgl-popup-anchor-top.dark-mode .mapboxgl-popup-tip':
            {
              borderBottomColor: '#121212 !important',
            },
          '.maplibregl-popup.custom-popup.maplibregl-popup-anchor-left.dark-mode .maplibregl-popup-tip, .mapboxgl-popup.custom-popup.mapboxgl-popup-anchor-left.dark-mode .mapboxgl-popup-tip':
            {
              borderRightColor: '#121212 !important',
            },
          '.maplibregl-popup.custom-popup.maplibregl-popup-anchor-right.dark-mode .maplibregl-popup-tip, .mapboxgl-popup.custom-popup.mapboxgl-popup-anchor-right.dark-mode .mapboxgl-popup-tip':
            {
              borderLeftColor: '#121212 !important',
            },

          '.maplibregl-ctrl-zoom-in, .maplibregl-ctrl-zoom-out': {
            transition: 'all 0.2s ease',
          },
        }}
      />
      <Map
        ref={mapRef}
        mapLib={maplibregl}
        initialViewState={{
          longitude: initialCenter[0],
          latitude: initialCenter[1],
          zoom: startPoint ? 8 : 8,
        }}
        style={{ width: '100%', height: '95vh' }}
        mapStyle={mergedStyle}
        onClick={onMapClick}
        crossSourceCollisions={false}
        onLoad={() => setMapLoaded(true)}
        maxZoom={18}
      >
        {/* Zoom controls (top-right like Leaflet example) */}
        <NavigationControl visualizePitch />

        {/* ===== Route line ===== */}
        {routeGeoJSON.features.length > 0 && (
          <Source id="route" type="geojson" data={routeGeoJSON}>
            <Layer
              id="route-line"
              type="line"
              paint={{
                'line-color': '#3C91E6',
                'line-width': 4,
              }}
            />
          </Source>
        )}

        {/* ===== Start & End markers ===== */}
        {startPoint && (
          <Marker
            longitude={startPoint[0]}
            latitude={startPoint[1]}
            anchor="center"
          >
            <div
              dangerouslySetInnerHTML={{ __html: makeStartEl().outerHTML }}
            />
          </Marker>
        )}
        {endPoint && (
          <Marker
            longitude={endPoint[0]}
            latitude={endPoint[1]}
            anchor="center"
          >
            <div dangerouslySetInnerHTML={{ __html: makeEndEl().outerHTML }} />
          </Marker>
        )}

        {/* ===== Waypoints (draggable, dblclick to remove) ===== */}
        {localWaypoints.map((wp, idx) => (
          <Marker
            key={`wp-${idx}`}
            longitude={wp[0]}
            latitude={wp[1]}
            draggable
            onDragEnd={(e) => {
              const { lng, lat } = e.lngLat;
              updateWaypoint(idx, [lng, lat]);
            }}
            onClick={(e: any) => {
              const evt = e?.originalEvent as MouseEvent | undefined;
              if (evt && evt.detail >= 2) {
                evt?.stopPropagation();
                removeWaypoint(idx);
              }
            }}
            anchor="center"
          >
            <div
              dangerouslySetInnerHTML={{
                __html: makeWaypointEl(idx).outerHTML,
              }}
              style={{ pointerEvents: 'none' }}
            />
          </Marker>
        ))}

        {/* ===== 50MAX layers ===== */}
        {!!fiftyMaxData?.length && (
          <Fragment>
            {/* Lines */}
            {fiftyMaxData[0]?.features?.length > 0 && (
              <Source id="fifty-lines" type="geojson" data={fiftyMaxData[0]}>
                <Layer
                  id="fifty-line-layer"
                  type="line"
                  paint={{ 'line-color': 'rgb(234,51,35)', 'line-width': 2 }}
                />
              </Source>
            )}

            {/* Red points */}
            {fiftyMaxData[1]?.features?.length > 0 &&
              (fiftyMaxData[1].features as GeoJSON.Feature[]).map((f, idx) => {
                if (f.geometry?.type !== 'Point') return null;
                const coord = (f.geometry as GeoJSON.Point).coordinates as [
                  number,
                  number,
                ];
                return (
                  <Marker
                    key={`fifty-red-${idx}`}
                    longitude={coord[0]}
                    latitude={coord[1]}
                    anchor="center"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      openFeaturePopup(coord, (f.properties as any) || {});
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundImage: `url(${dncMarkerUrl})`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        cursor: 'pointer',
                      }}
                    />
                  </Marker>
                );
              })}

            {/* Black points (rendered same as red in your code with forceHighRiskStyle) */}
            {fiftyMaxData[2]?.features?.length > 0 &&
              (fiftyMaxData[2].features as GeoJSON.Feature[]).map((f, idx) => {
                if (f.geometry?.type !== 'Point') return null;
                const coord = (f.geometry as GeoJSON.Point).coordinates as [
                  number,
                  number,
                ];

                return (
                  <Marker
                    key={`fifty-black-${idx}`}
                    longitude={coord[0]}
                    latitude={coord[1]}
                    anchor="center"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      openFeaturePopup(coord, (f.properties as any) || {});
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundImage: `url(${dncMarkerUrl})`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        cursor: 'pointer',
                      }}
                    />
                  </Marker>
                );
              })}

            {/* Polygons (filtering out statusExternal === 'Part of 50MAX') */}
            {fiftyMaxData[3]?.features?.length > 0 && (
              <Source
                id="fifty-polygons"
                type="geojson"
                data={{
                  ...fiftyMaxData[3],
                  features: fiftyMaxData[3].features.filter(
                    (f: any) =>
                      f?.properties?.statusExternal !== 'Part of 50MAX',
                  ),
                }}
              >
                <Layer
                  id="fifty-polygons-fill"
                  type="fill"
                  paint={{
                    'fill-color': 'rgba(234,51,35,0.1)',
                    'fill-opacity': 0.4,
                  }}
                />
                <Layer
                  id="fifty-polygons-line"
                  type="line"
                  paint={{ 'line-color': 'rgba(234,51,35,1)', 'line-width': 1 }}
                />
              </Source>
            )}
          </Fragment>
        )}

        {/* ===== HPMV layers ===== */}
        {!!hpmvData?.length && (
          <Fragment>
            {/* Line 1 */}
            {hpmvData[0]?.features?.length > 0 && (
              <Source id="hpmv-line1" type="geojson" data={hpmvData[0]}>
                <Layer
                  id="hpmv-line1-layer"
                  type="line"
                  paint={{ 'line-color': 'rgb(31,110,23)', 'line-width': 3 }}
                />
              </Source>
            )}
            {/* Line 2 */}
            {hpmvData[1]?.features?.length > 0 && (
              <Source id="hpmv-line2" type="geojson" data={hpmvData[1]}>
                <Layer
                  id="hpmv-line2-layer"
                  type="line"
                  paint={{ 'line-color': '#73c2fb', 'line-width': 2 }}
                />
              </Source>
            )}
            {/* Points */}
            {hpmvData[2]?.features?.length > 0 &&
              (hpmvData[2].features as GeoJSON.Feature[]).map((f, idx) => {
                if (f.geometry?.type !== 'Point') return null;
                const coord = (f.geometry as GeoJSON.Point).coordinates as [
                  number,
                  number,
                ];
                return (
                  <Marker
                    key={`hpmv-pt-${idx}`}
                    longitude={coord[0]}
                    latitude={coord[1]}
                    anchor="center"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      openFeaturePopup(coord, (f.properties as any) || {});
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundImage: `url(${dncMarkerUrl})`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        cursor: 'pointer',
                      }}
                    />
                  </Marker>
                );
              })}
          </Fragment>
        )}

        {/* ===== Stadia Vector Tileset (NZ Admins) ===== */}
        {showRegionalCouncils && (
          <Source
            id="nz-regional-councils"
            type="vector"
            tiles={[
              `https://tiles.stadiamaps.com/data/c_nz_admins/{z}/{x}/{y}.pbf`,
            ]}
            maxzoom={14}
          >
            <Layer
              id="regional-councils-fill"
              type="fill"
              source-layer="territorial_authority"
              paint={{
                'fill-color': [
                  'case',
                  ...selectedRegionalCouncils.flatMap((council) => [
                    ['==', ['get', 'TA2025_V_1'], council.name],
                    council.hasGeojsonData
                      ? 'rgba(76, 175, 80, 0.3)'
                      : council.hasUploads
                        ? 'rgba(246, 174, 45, 0.2)'
                        : 'rgba(0, 0, 0, 0)',
                  ]),
                  'rgba(200,200,200,0.1)',
                ] as any,
                'fill-opacity': [
                  'case',
                  selectedRegionalCouncils.length > 0
                    ? [
                        'in',
                        ['get', 'TA2025_V_1'],
                        [
                          'literal',
                          selectedRegionalCouncils.map((s) => s.name),
                        ],
                      ]
                    : ['literal', true],
                  0.6,
                  0,
                ],
              }}
            />
            <Layer
              id="regional-councils-line"
              type="line"
              source-layer="territorial_authority"
              paint={{
                'line-color': [
                  'case',
                  ...selectedRegionalCouncils.flatMap((council) => [
                    ['==', ['get', 'TA2025_V_1'], council.name],
                    council.hasGeojsonData
                      ? 'rgba(76, 175, 80, 0.7)'
                      : 'rgba(246, 174, 45, 0.6)',
                  ]),
                  'rgba(0,0,0,0)', // default color
                ] as any,
                'line-width': [
                  'case',
                  selectedRegionalCouncils.length > 0
                    ? [
                        'in',
                        ['get', 'TA2025_V_1'],
                        [
                          'literal',
                          selectedRegionalCouncils.map((s) => s.name),
                        ],
                      ]
                    : ['literal', true],
                  2,
                  0,
                ],
              }}
            />
          </Source>
        )}

        {showTerritorialAuthorities && (
          <Source
            id="nz-territorial-authorities"
            type="vector"
            tiles={[
              `https://tiles.stadiamaps.com/data/c_nz_admins/{z}/{x}/{y}.pbf`,
            ]}
            maxzoom={14}
          >
            <Layer
              id="territorial-authorities-line"
              type="line"
              source-layer="territorial_authority"
              paint={{
                'line-color': [
                  'case',
                  selectedTerritorialAuthorities.length > 0
                    ? [
                        'in',
                        ['get', 'TA2025_V_1'],
                        ['literal', selectedTerritorialAuthorities],
                      ]
                    : ['literal', true],
                  'rgba(180,160,220,0.8)',
                  'rgba(0,0,0,0)',
                ],
                'line-width': [
                  'case',
                  selectedTerritorialAuthorities.length > 0
                    ? [
                        'in',
                        ['get', 'TA2025_V_1'],
                        ['literal', selectedTerritorialAuthorities],
                      ]
                    : ['literal', true],
                  2,
                  0,
                ],
              }}
            />
          </Source>
        )}

        {/* ===== Popup ===== */}
        {popupInfo && (
          <Popup
            className={`custom-popup ${mode}-mode`}
            longitude={popupInfo.lngLat[0]}
            latitude={popupInfo.lngLat[1]}
            onClose={() => setPopupInfo(null)}
            offset={16}
            closeOnMove={true}
            anchor="bottom"
          >
            <div dangerouslySetInnerHTML={{ __html: popupInfo.html }} />
          </Popup>
        )}
      </Map>
    </Box>
  );
};

export default MapStadia_MapLibre;
